type ReducerAction = {
  type?: string;
  [key: string]: any;
};

export type LedgerRecordsError = {
  code: string;
  [key: string]: unknown;
};

export type LedgerRecordsEntry = {
  recordMap: Record<string, unknown>;
  isLoading: boolean;
  error: LedgerRecordsError | null;
  isAppending: boolean;
  appendError: LedgerRecordsError | null;
};

export type LedgerRecordsState = {
  byId: Record<string, LedgerRecordsEntry>;
};

const emptyEntry = (): LedgerRecordsEntry => {
  return {
    recordMap: {},
    isLoading: false,
    error: null,
    isAppending: false,
    appendError: null,
  };
};

const entryFor = (
  state: LedgerRecordsState,
  ledgerId: string
): LedgerRecordsEntry => {
  return state.byId[ledgerId] || emptyEntry();
};

const initialState: LedgerRecordsState = { byId: {} };

const ledgerRecordsReducer = (
  stateArg: LedgerRecordsState | undefined,
  action: ReducerAction
): LedgerRecordsState => {
  const state = stateArg || initialState;
  switch (action.type) {
    case 'AUTH_LOGOUT':
    case 'AUTH_LOGOUT_SUCCESS': {
      return initialState;
    }
    case 'LEDGERS_DELETE':
    case 'LEDGERS_FINISHED_DELETING': {
      const ledgerId = action.ledgerId || action.payload?.ledgerId;
      if (!ledgerId || !state.byId[ledgerId]) {
        return state;
      }
      const { [ledgerId]: _removed, ...rest } = state.byId;
      return { byId: rest };
    }
    case 'LEDGER_RECORDS_IS_LOADING': {
      const { ledgerId } = action;
      return {
        byId: {
          ...state.byId,
          [ledgerId]: {
            ...entryFor(state, ledgerId),
            isLoading: true,
            error: null,
          },
        },
      };
    }
    case 'LEDGER_RECORDS_FINISHED_LOADING': {
      const { ledgerId, recordMap } = action;
      return {
        byId: {
          ...state.byId,
          [ledgerId]: {
            ...entryFor(state, ledgerId),
            recordMap: recordMap || {},
            isLoading: false,
            error: null,
          },
        },
      };
    }
    case 'LEDGER_RECORDS_FINISHED_LOADING_WITH_ERROR': {
      const { ledgerId, error } = action;
      return {
        byId: {
          ...state.byId,
          [ledgerId]: {
            ...entryFor(state, ledgerId),
            isLoading: false,
            error,
          },
        },
      };
    }
    case 'LEDGER_RECORDS_IS_APPENDING': {
      const { ledgerId } = action;
      return {
        byId: {
          ...state.byId,
          [ledgerId]: {
            ...entryFor(state, ledgerId),
            isAppending: true,
            appendError: null,
          },
        },
      };
    }
    case 'LEDGER_RECORDS_FINISHED_APPENDING': {
      const { ledgerId, event } = action;
      const current = entryFor(state, ledgerId);
      return {
        byId: {
          ...state.byId,
          [ledgerId]: {
            ...current,
            recordMap: { ...current.recordMap, [event.id]: event },
            isAppending: false,
            appendError: null,
          },
        },
      };
    }
    case 'LEDGER_RECORDS_FINISHED_APPENDING_WITH_ERROR': {
      const { ledgerId, error } = action;
      return {
        byId: {
          ...state.byId,
          [ledgerId]: {
            ...entryFor(state, ledgerId),
            isAppending: false,
            appendError: error,
          },
        },
      };
    }
    default:
      return state;
  }
};

export default ledgerRecordsReducer;
